import axiosInstance from "core/api/axiosInstance";
import axios from "axios";

// Obtener todos los readers
export const getReadersService = async () => {
  try {
    const { data } = await axiosInstance.get(`/api/automatization/get-readers`, {
      timeout: 30000,
    });
    return data;
  } catch (error) {
    console.error("Error en getReadersService:", error);

    if (error.response) {
      throw new Error(
        error.response.data?.message ||
          `Error ${error.response.status}: ${error.response.statusText}`
      );
    } else if (error.request) {
      throw new Error("No se pudo conectar con el servidor. Verifica tu conexión.");
    } else {
      throw new Error("Error al configurar la solicitud");
    }
  }
};

// Reiniciar TODOS los switches
export const resetAllReadersService = async () => {
  try {
    const config = {
      timeout: 0,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const startTime = Date.now();

    const response = await axiosInstance.post(`/api/automatization/reset-reader-all`, {}, config);

    const endTime = Date.now();
    const duration = Math.round((endTime - startTime) / 1000);
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    const durationFormatted = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;

    switch (response.status) {
      case 200:
      case 201:
        return {
          success: true,
          data: response.data,
          message:
            response.data?.message ||
            `✅ Reinicio masivo completado exitosamente en ${durationFormatted}.`,
          timestamp: new Date().toISOString(),
          duration: duration,
          durationFormatted: durationFormatted,
          stats: response.data?.stats || {
            total: response.data?.totalProcessed || 0,
            success: response.data?.successCount || 0,
            failed: response.data?.failedCount || 0,
            skipped: response.data?.skippedCount || 0,
          },
          processedCount: response.data?.processedCount || 0,
          failedCount: response.data?.failedCount || 0,
          successCount: response.data?.successCount || 0,
          totalReaders: response.data?.totalReaders || 0,
          summary: response.data?.summary || {},
          rawData: response.data,
        };

      case 202:
        return {
          success: true,
          data: response.data,
          message:
            response.data?.message ||
            `🔄 Reinicio masivo aceptado y en proceso. ` +
              `La operación se está ejecutando en segundo plano.`,
          timestamp: new Date().toISOString(),
          status: "processing",
          isBackground: true,
          jobId: response.data?.jobId,
          operationId: response.data?.operationId,
          estimatedCompletion: response.data?.estimatedCompletion,
          estimatedDuration: response.data?.estimatedDuration,
          checkStatusUrl: response.data?.checkStatusUrl,
          progressUrl: response.data?.progressUrl,
          initialCount: response.data?.initialCount || 0,
        };

      case 400:
        throw new Error(response.data?.message || "❌ Solicitud inválida para reinicio masivo.");

      case 401:
      case 403:
        throw new Error("Acción no permitida, reinicio en proceso");

      case 404:
        throw new Error(response.data?.message || "❌ No se encontraron readers para reiniciar.");

      case 409:
        throw new Error(
          response.data?.message || "⚠️ Ya existe una operación de reinicio masivo en progreso."
        );

      case 422:
        throw new Error(
          response.data?.message || "❌ Error de validación en la solicitud de reinicio masivo."
        );

      case 429:
        throw new Error(
          "⏳ Demasiadas solicitudes de reinicio. " +
            "Por favor, espere antes de intentar nuevamente."
        );

      case 500:
      case 502:
      case 503:
        throw new Error(
          response.data?.message ||
            "🛑 Error del servidor durante el reinicio masivo. " + "Por favor, intente más tarde."
        );

      case 504:
        return {
          success: true,
          data: response.data,
          message:
            `⏱️ El reinicio masivo está tomando más tiempo de lo esperado. ` +
            `La operación continúa ejecutándose en el servidor.`,
          timestamp: new Date().toISOString(),
          status: "timeout_but_processing",
          warning: "El servidor no respondió a tiempo, pero la operación puede continuar.",
          estimatedRemaining: response.data?.estimatedRemaining,
        };

      default:
        throw new Error(
          response.data?.message ||
            `❌ Error ${response.status}: No se pudo iniciar el reinicio masivo.`
        );
    }
  } catch (error) {
    console.error(`❌ Error en resetAllReadersService:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ERR_NETWORK") {
        throw new Error(
          "🌐 Error de conexión durante el reinicio masivo. " + "Verifique su conexión a internet."
        );
      }

      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 507:
            throw new Error("💾 Servidor sin espacio suficiente para procesar el reinicio masivo.");
          case 509:
            throw new Error(
              "📊 Límite de ancho de banda excedido. Intente en un horario con menor tráfico."
            );
          default:
            throw new Error(data?.message || `❌ Error ${status}: ${error.message}`);
        }
      }

      if (!error.response) {
        throw new Error(
          "🔌 No se pudo conectar con el servidor para realizar el reinicio masivo. " +
            "Verifique su conexión y que el servidor esté disponible."
        );
      }
    }

    throw new Error(
      error.message || "❌ Error desconocido al intentar reinicio masivo de dispositivos."
    );
  }
};

export const resetReaderByIdService = async (hostId) => {
  if (!hostId) {
    throw new Error("❌ ID del dispositivo no válido.");
  }

  let id;
  try {
    id = typeof hostId === "number" ? hostId.toString() : String(hostId).trim();

    if (!/^[a-zA-Z0-9\-_]+$/.test(id)) {
      throw new Error(
        "❌ Formato de ID inválido. Use solo letras, números, guiones y guiones bajos."
      );
    }

    if (id.length < 1 || id.length > 50) {
      throw new Error("❌ ID debe tener entre 1 y 50 caracteres.");
    }
  } catch (validationError) {
    throw new Error(`❌ Error de validación de ID: ${validationError.message}`);
  }

  try {
    const config = {
      timeout: 0,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const startTime = Date.now();

    const response = await axiosInstance.post(
      `/api/automatization/reset-reader-id`,
      { hostId: id },
      config
    );

    const endTime = Date.now();
    const duration = Math.round((endTime - startTime) / 1000);

    switch (response.status) {
      case 200:
      case 201:
        return {
          success: true,
          data: response.data,
          message:
            response.data?.message || `✅ Reader reiniciado exitosamente en ${duration} segundos.`,
          timestamp: new Date().toISOString(),
          hostId: id,
          duration: duration,
          status: "completed",
          details: {
            resetDate: response.data?.resetDate,
            resetResponse: response.data?.resetResponse,
            deviceName: response.data?.deviceName,
            ipAddress: response.data?.ipAddress,
            commerce: response.data?.commerce,
          },
          rawData: response.data,
        };

      case 202:
        return {
          success: true,
          data: response.data,
          message:
            response.data?.message || `🔄 Reinicio del reader ${id} iniciado en segundo plano.`,
          timestamp: new Date().toISOString(),
          hostId: id,
          status: "processing",
          operationId: response.data?.operationId,
          estimatedTime: response.data?.estimatedTime,
          progressUrl: response.data?.progressUrl,
        };

      case 400:
        throw new Error(response.data?.message || "❌ Solicitud inválida. Revisa los datos.");

      case 401:
        throw new Error("Sesión expirada. Por favor, inicie sesión nuevamente.");

      case 403:
        throw new Error("Acción no permitida, reinicio en proceso");

      case 404:
        throw new Error(`Dispositivo con ID "${id}" no encontrado.`);

      case 409:
        throw new Error(
          response.data?.message ||
            "⚠️ Operación en conflicto. El dispositivo ya está siendo reiniciado."
        );

      case 422:
        throw new Error(
          response.data?.message || "❌ Datos inválidos. Verifique la información enviada."
        );

      case 429:
        throw new Error(
          "⏳ Demasiadas solicitudes. Por favor, espere antes de intentar nuevamente."
        );

      case 500:
        throw new Error("🛑 Error interno del servidor. Intente más tarde.");

      case 502:
        throw new Error("🔧 Servidor temporalmente no disponible. Intente más tarde.");

      case 503:
        throw new Error("🚧 Servicio en mantenimiento. Intente más tarde.");

      case 504:
        return {
          success: true,
          data: response.data,
          message:
            `⏱️ El reinicio del reader ${id} está tomando más tiempo de lo esperado. ` +
            `La operación continúa en el servidor.`,
          timestamp: new Date().toISOString(),
          hostId: id,
          status: "timeout_but_processing",
          warning: "El servidor no respondió a tiempo, pero la operación puede continuar.",
        };

      default:
        throw new Error(
          response.data?.message ||
            `❌ Error ${response.status}: No se pudo completar la operación.`
        );
    }
  } catch (error) {
    console.error(`❌ Error en resetReaderByIdService para reader ${id}:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ERR_NETWORK") {
        throw new Error("🌐 Error de red. Verifique su conexión a internet.");
      }

      if (error.response) {
        const { status, data } = error.response;
        const errorMsg = data?.message || `Error ${status}`;

        switch (status) {
          case 0:
            throw new Error("🔌 No se pudo conectar con el servidor.");
          case 408:
            throw new Error("⏱️ Tiempo de espera agotado. El servidor no respondió.");
          default:
            throw new Error(`❌ ${errorMsg}`);
        }
      }

      if (!error.response && error.request) {
        throw new Error("🔌 No hay respuesta del servidor. Verifique la conexión.");
      }
    }

    if (error instanceof Error) {
      if (error.message.includes("Network Error")) {
        throw new Error("🌐 Error de red. Verifique su conexión.");
      }
      throw error;
    }

    throw new Error("❌ Error desconocido al reiniciar el dispositivo.");
  }
};

export const sendPulseToAntennaService = async (antennaId, duration = null) => {
  if (!antennaId) {
    throw new Error("❌ ID de antena no válido.");
  }

  const id = typeof antennaId === "string" ? parseInt(antennaId) : antennaId;

  if (isNaN(id) || id <= 0) {
    throw new Error("❌ ID de antena no válido. Debe ser un número positivo.");
  }

  try {
    const config = {
      timeout: 30000,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const requestBody = {
      id: id,
    };

    if (duration !== null && duration !== undefined) {
      const durationNum = Number(duration);
      if (!isNaN(durationNum) && durationNum > 0) {
        requestBody.duration = durationNum;
      }
    }

    const startTime = Date.now();
    const response = await axiosInstance.post(
      `/api/automatization/pulse-reader`,
      requestBody,
      config
    );
    const endTime = Date.now();
    const responseDuration = Math.round((endTime - startTime) / 1000);

    const responseData = response.data?.response?.[0];
    const isSuccess =
      response.data?.msm === "successful" ||
      response.data?.msm === "succesful" ||
      response.status === 200;

    if (isSuccess) {
      return {
        success: true,
        data: response.data,
        message: response.data?.msm || "✅ Pulso enviado exitosamente.",
        timestamp: new Date().toISOString(),
        antennaId: responseData?.id || id,
        duration: responseDuration,
        requestedDuration: requestBody.duration || null,
        resetDate: responseData?.more?.resetdate,
        resetResponse: responseData?.more?.resetresponse,
        antennaName: responseData?.namepoint || `Antena ${id}`,
        port: responseData?.point || "N/A",
        rawData: responseData,
      };
    }

    switch (response.status) {
      case 400:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "❌ Solicitud inválida. Verifique el ID de la antena."
        );

      case 401:
      case 403:
        throw new Error(
          "🔒 No tiene permisos para enviar pulsos a antenas. Por favor, inicie sesión nuevamente."
        );

      case 404:
        throw new Error(`❌ Antena con ID ${id} no encontrada. Verifique el ID de la antena.`);

      case 409:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "⚠️ La antena ya está procesando un pulso. Espere a que termine la operación anterior."
        );

      case 422:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "❌ Error de validación en los datos enviados."
        );

      case 500:
      case 502:
      case 503:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "🛑 Error del servidor. Por favor, intente nuevamente más tarde."
        );

      default:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            `❌ Error ${response.status}: No se pudo enviar el pulso a la antena.`
        );
    }
  } catch (error) {
    console.error(`❌ Error en sendPulseToAntennaService para antena ${antennaId}:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ERR_NETWORK") {
        throw new Error("🌐 Error de conexión. Verifique su conexión a internet.");
      }

      if (error.response) {
        const { status, data } = error.response;
        const errorMsg = data?.msm || data?.message || `Error ${status}: ${error.message}`;
        throw new Error(errorMsg);
      }

      if (!error.response) {
        throw new Error(
          "🔌 No se pudo conectar con el servidor. " +
            "Verifique su conexión y que el servidor esté disponible."
        );
      }
    }

    throw new Error(error.message || "❌ Error desconocido al enviar pulso a la antena.");
  }
};

export const resetReaderCommerceService = async (commerce) => {
  if (!commerce || typeof commerce !== "string") {
    throw new Error("Nombre del comercio no válido.");
  }

  try {
    const config = {
      timeout: 0,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const response = await axiosInstance.post(
      `/api/automatization/reset-reader-commerce`,
      { commerce },
      config
    );

    switch (response.status) {
      case 200:
      case 201:
        return {
          success: true,
          data: response.data,
          message:
            response.data?.message ||
            `Dispositivos del comercio ${commerce} reiniciados exitosamente.`,
          timestamp: new Date().toISOString(),
          commerce: commerce,
          failedCount: response.data?.failedCount || 0,
          totalProcessed: response.data?.totalProcessed || 0,
        };

      case 202:
        return {
          success: true,
          data: response.data,
          message:
            response.data?.message ||
            `Solicitud de reinicio para comercio ${commerce} aceptada y en proceso.`,
          timestamp: new Date().toISOString(),
          commerce: commerce,
          status: "processing",
          estimatedTime: response.data?.estimatedTime,
        };

      case 400:
        throw new Error(
          response.data?.message || "Solicitud inválida. Verifique los datos enviados."
        );

      case 401:
      case 403:
        throw new Error("Acción no permitida, reinicio en proceso.");

      case 404:
        throw new Error("Comercio no encontrado. Verifique el nombre del comercio.");

      case 409:
        throw new Error(
          response.data?.message || "Los dispositivos ya están en proceso de reinicio."
        );

      case 422:
        throw new Error(response.data?.message || "Error de validación en los datos enviados.");

      case 500:
      case 502:
      case 503:
        throw new Error(
          response.data?.message || "Error del servidor. Por favor, intente nuevamente más tarde."
        );

      default:
        throw new Error(
          response.data?.message ||
            `Error ${response.status}: No se pudo reiniciar los dispositivos.`
        );
    }
  } catch (error) {
    console.error(`Error en resetReaderCommerceService para comercio ${commerce}:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ERR_NETWORK") {
        throw new Error("Error de conexión. Verifique su conexión a internet.");
      }

      if (error.response) {
        const { status, data } = error.response;
        throw new Error(data?.message || `Error ${status}: ${error.message}`);
      }

      if (!error.response) {
        throw new Error("No se pudo conectar con el servidor. Verifique su conexión.");
      }
    }

    throw new Error(error.message || "Error desconocido al reiniciar los dispositivos.");
  }
};

export const getReaderInform = async () => {
  try {
    const config = {
      timeout: 60000,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const startTime = Date.now();

    const response = await axiosInstance.get(`/api/automatization/get-reader-report`, config);

    const endTime = Date.now();
    const duration = Math.round((endTime - startTime) / 1000);

    switch (response.status) {
      case 200:
        return {
          success: true,
          data: response.data?.data || [],
          message: `✅ Informe obtenido exitosamente. ${
            response.data?.data?.length || 0
          } registros encontrados.`,
          timestamp: new Date().toISOString(),
          duration: duration,
          totalRecords: response.data?.data?.length || 0,
          metadata: response.data?.metadata || {},
          rawData: response.data,
        };

      case 204:
        return {
          success: true,
          data: [],
          message: "📭 No hay datos de readers para generar el informe.",
          timestamp: new Date().toISOString(),
          totalRecords: 0,
          isEmpty: true,
        };

      case 400:
        throw new Error(response.data?.message || "❌ Solicitud de informe inválida.");

      case 401:
      case 403:
        throw new Error("Acción no permitida, reinicio en proceso");

      case 404:
        throw new Error("❌ Endpoint de informe no encontrado.");

      case 429:
        throw new Error(
          "⏳ Demasiadas solicitudes de informe. Por favor, espere antes de intentar nuevamente."
        );

      case 500:
      case 502:
      case 503:
        throw new Error(
          response.data?.message ||
            "🛑 Error del servidor al generar el informe. Por favor, intente más tarde."
        );

      default:
        throw new Error(
          response.data?.message || `❌ Error ${response.status}: No se pudo obtener el informe.`
        );
    }
  } catch (error) {
    console.error(`❌ Error en getReaderInform:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ECONNABORTED") {
        throw new Error("⏱️ Tiempo de espera agotado. El servidor no respondió.");
      }
      if (error.code === "ERR_NETWORK") {
        throw new Error("🌐 Error de conexión. Verifique su conexión a internet.");
      }
      if (error.response) {
        const { status, data } = error.response;
        throw new Error(data?.message || `❌ Error ${status}: ${error.message}`);
      }
      if (!error.response) {
        throw new Error("🔌 No se pudo conectar con el servidor.");
      }
    }

    throw new Error(error.message || "❌ Error desconocido al obtener el informe.");
  }
};

export const getAntennaPowerService = async (antennaId) => {
  if (!antennaId) {
    throw new Error("❌ ID de antena no válido.");
  }

  let id;
  try {
    id = typeof antennaId === "number" ? antennaId.toString() : String(antennaId).trim();

    if (!/^[0-9]+$/.test(id)) {
      throw new Error("❌ Formato de ID inválido. Use solo números.");
    }

    if (id.length < 1 || id.length > 10) {
      throw new Error("❌ ID debe tener entre 1 y 10 caracteres.");
    }
  } catch (validationError) {
    throw new Error(`❌ Error de validación de ID: ${validationError.message}`);
  }

  try {
    const config = {
      timeout: 30000,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const startTime = Date.now();
    const response = await axiosInstance.get(`/api/automatization/get-power-antenna/${id}`, config);
    const endTime = Date.now();
    const duration = Math.round((endTime - startTime) / 1000);

    switch (response.status) {
      case 200:
      case 201:
        const responseData = response.data?.response || {};
        const rawPower = parseInt(responseData.power) || 0;
        const formattedPower = (rawPower / 100).toFixed(1);

        return {
          success: true,
          data: response.data,
          message: response.data?.msm || "Potencia obtenida exitosamente.",
          timestamp: new Date().toISOString(),
          antennaId: responseData?.id || id,
          duration: duration,
          power: rawPower,
          formattedPower: formattedPower,
          powerMessage: responseData?.message || "",
          rawData: responseData,
        };

      case 202:
        return {
          success: true,
          data: response.data,
          message:
            response.data?.msm ||
            `🔄 Solicitud de potencia para antena ${id} aceptada. ` +
              `El proceso se está ejecutando en segundo plano.`,
          timestamp: new Date().toISOString(),
          antennaId: id,
          status: "processing",
          estimatedCompletion: response.data?.estimatedCompletion,
        };

      case 400:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "❌ Solicitud inválida. Verifique el ID de la antena."
        );

      case 401:
      case 403:
        throw new Error(
          "No tiene permisos para consultar la potencia de antenas. Por favor, inicie sesión nuevamente."
        );

      case 404:
        throw new Error(`Antena con ID ${id} no encontrada. Verifique el ID de la antena.`);

      case 422:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "Error de validación en los datos enviados."
        );

      case 500:
      case 502:
      case 503:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "Error del servidor. Por favor, intente nuevamente más tarde."
        );

      default:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            `Error ${response.status}: No se pudo obtener la potencia.`
        );
    }
  } catch (error) {
    console.error(`Error en getAntennaPowerService para antena ${antennaId}:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ECONNABORTED") {
        throw new Error("⏱️ Tiempo de espera agotado. El servidor no respondió.");
      }
      if (error.code === "ERR_NETWORK") {
        throw new Error("🌐 Error de conexión. Verifique su conexión a internet.");
      }

      if (error.response) {
        const { status, data } = error.response;
        const errorMsg = data?.msm || data?.message || `Error ${status}: ${error.message}`;
        throw new Error(errorMsg);
      }

      if (!error.response) {
        throw new Error(
          "🔌 No se pudo conectar con el servidor. " +
            "Verifique su conexión y que el servidor esté disponible."
        );
      }
    }

    throw new Error(error.message || "Error desconocido al obtener la potencia de la antena.");
  }
};

export const setAntennaPowerService = async (antennaId, powerLevel, readerType = "zebra") => {
  if (!antennaId) {
    throw new Error("ID de antena no válido.");
  }

  if (powerLevel === undefined || powerLevel === null) {
    throw new Error("Nivel de potencia no válido.");
  }

  let id;
  try {
    id = typeof antennaId === "string" ? parseInt(antennaId) : antennaId;

    if (isNaN(id) || id <= 0) {
      throw new Error("❌ ID de antena debe ser un número positivo.");
    }
  } catch (validationError) {
    throw new Error(`❌ Error de validación de ID: ${validationError.message}`);
  }

  const isHura = readerType === "hura";

  try {
    const config = {
      timeout: 30000,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const levelToSend = isHura ? powerLevel : powerLevel;

    const requestData = {
      id: id,
      level: levelToSend,
    };

    const startTime = Date.now();
    const response = await axiosInstance.post(
      `/api/automatization/set-power-antenna`,
      requestData,
      config
    );
    const endTime = Date.now();
    const duration = Math.round((endTime - startTime) / 1000);

    const responseData = response.data?.response || {};
    const isSuccess =
      response.data?.success === true ||
      response.data?.msm === "succesful" ||
      (response.status >= 200 && response.status < 300);

    if (isSuccess) {
      let rawPower = parseInt(responseData.power);
      let formattedPower;
      let powerForStorage;

      if (isHura) {
        powerForStorage = rawPower || powerLevel;
        formattedPower = powerForStorage.toString();
      } else {
        powerForStorage = rawPower || powerLevel;
        formattedPower = (powerForStorage / 100).toFixed(1);
      }

      return {
        success: true,
        data: response.data,
        message: response.data?.msm || "Potencia actualizada exitosamente.",
        timestamp: new Date().toISOString(),
        antennaId: responseData?.id || id,
        duration: duration,
        power: powerForStorage,
        formattedPower: formattedPower,
        powerMessage: responseData?.message || "Potencia actualizada correctamente.",
        readerType: readerType,
        rawData: responseData,
      };
    }

    switch (response.status) {
      case 400:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "❌ Solicitud inválida. Verifique el ID de la antena y el nivel de potencia."
        );

      case 401:
      case 403:
        throw new Error(
          "🔒 No tiene permisos para configurar la potencia de antenas. Por favor, inicie sesión nuevamente."
        );

      case 404:
        throw new Error(`❌ Antena con ID ${id} no encontrada. Verifique el ID de la antena.`);

      case 409:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "La antena ya está procesando una configuración de potencia. Espere a que termine la operación anterior."
        );

      case 422:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "Error de validación en los datos enviados. Verifique los valores de potencia."
        );

      case 500:
      case 502:
      case 503:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            "Error del servidor. Por favor, intente nuevamente más tarde."
        );

      default:
        throw new Error(
          response.data?.msm ||
            response.data?.message ||
            `Error ${response.status}: No se pudo configurar la potencia de la antena.`
        );
    }
  } catch (error) {
    console.error(`Error en setAntennaPowerService para antena ${antennaId}:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ECONNABORTED") {
        throw new Error("⏱️ Tiempo de espera agotado. El servidor no respondió.");
      }
      if (error.code === "ERR_NETWORK") {
        throw new Error("🌐 Error de conexión. Verifique su conexión a internet.");
      }

      if (error.response) {
        const { status, data } = error.response;
        const errorMsg = data?.msm || data?.message || `Error ${status}: ${error.message}`;
        throw new Error(errorMsg);
      }

      if (!error.response) {
        throw new Error(
          "🔌 No se pudo conectar con el servidor. " +
            "Verifique su conexión y que el servidor esté disponible."
        );
      }
    }

    throw new Error(error.message || "Error desconocido al configurar la potencia de la antena.");
  }
};

// ============================================
// SERVICIO: OBTENER ESTADO DE LA ANTENA (ENCENDIDA/APAGADA)
// ============================================
export const getAntennaStatusService = async (antennaId) => {
  if (!antennaId) {
    throw new Error("❌ ID de antena no válido.");
  }

  let id;
  try {
    id = typeof antennaId === "number" ? antennaId.toString() : String(antennaId).trim();

    if (!/^[0-9]+$/.test(id)) {
      throw new Error("❌ Formato de ID inválido. Use solo números.");
    }
  } catch (validationError) {
    throw new Error(`❌ Error de validación de ID: ${validationError.message}`);
  }

  try {
    const config = {
      timeout: 30000,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const response = await axiosInstance.get(`/api/automatization/get-status-reader/${id}`, config);

    switch (response.status) {
      case 200:
        return {
          success: true,
          data: response.data,
          message: response.data?.msm || "Estado obtenido exitosamente.",
          timestamp: new Date().toISOString(),
          antennaId: id,
          status: response.data?.data?.data?.status || "Desconocido",
          rawData: response.data,
        };

      case 400:
        throw new Error(
          response.data?.msm || "❌ Solicitud inválida. Verifique el ID de la antena."
        );

      case 401:
      case 403:
        throw new Error("🔒 No tiene permisos para consultar el estado de antenas.");

      case 404:
        throw new Error(`❌ Antena con ID ${id} no encontrada.`);

      case 500:
      case 502:
      case 503:
        throw new Error("🛑 Error del servidor. Por favor, intente nuevamente más tarde.");

      default:
        throw new Error(
          response.data?.msm || `❌ Error ${response.status}: No se pudo obtener el estado.`
        );
    }
  } catch (error) {
    console.error(`❌ Error en getAntennaStatusService para antena ${antennaId}:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ECONNABORTED") {
        throw new Error("⏱️ Tiempo de espera agotado. El servidor no respondió.");
      }
      if (error.code === "ERR_NETWORK") {
        throw new Error("🌐 Error de conexión. Verifique su conexión a internet.");
      }

      if (error.response) {
        const { status, data } = error.response;
        const errorMsg = data?.msm || data?.message || `Error ${status}: ${error.message}`;
        throw new Error(errorMsg);
      }

      if (!error.response) {
        throw new Error("🔌 No se pudo conectar con el servidor.");
      }
    }

    throw new Error(error.message || "❌ Error desconocido al obtener el estado de la antena.");
  }
};

// ============================================
// SERVICIO: ENCENDER / APAGAR ANTENA
// ============================================
export const setAntennaPowerStatusService = async (antennaId, state) => {
  if (!antennaId) {
    throw new Error("❌ ID de antena no válido.");
  }

  if (!state || (state !== "on" && state !== "off")) {
    throw new Error("❌ Estado no válido. Debe ser 'on' u 'off'.");
  }

  let id;
  try {
    id = typeof antennaId === "number" ? antennaId : parseInt(antennaId);

    if (isNaN(id) || id <= 0) {
      throw new Error("❌ ID de antena debe ser un número positivo.");
    }
  } catch (validationError) {
    throw new Error(`❌ Error de validación de ID: ${validationError.message}`);
  }

  try {
    const config = {
      timeout: 30000,
      validateStatus: (status) => status >= 200 && status < 500,
    };

    const response = await axiosInstance.post(
      `/api/automatization/set-power-status-reader`,
      { id: id, state: state },
      config
    );

    const isSuccess =
      response.data?.msm === "succesful" ||
      response.data?.msm === "successful" ||
      response.data?.data?.status === 200 ||
      (response.status >= 200 && response.status < 300);

    if (isSuccess) {
      return {
        success: true,
        data: response.data,
        message:
          response.data?.msm ||
          `✅ Antena ${state === "on" ? "encendida" : "apagada"} exitosamente.`,
        timestamp: new Date().toISOString(),
        antennaId: id,
        newStatus: state === "on" ? "Encendida" : "Apagada",
        rawData: response.data,
      };
    }

    switch (response.status) {
      case 400:
        throw new Error(
          response.data?.msm || "❌ Solicitud inválida. Verifique el ID y el estado."
        );
      case 401:
      case 403:
        throw new Error("🔒 No tiene permisos para cambiar el estado de la antena.");
      case 404:
        throw new Error(`❌ Antena con ID ${id} no encontrada.`);
      case 500:
      case 502:
      case 503:
        throw new Error("🛑 Error del servidor. Intente más tarde.");
      default:
        throw new Error(
          response.data?.msm || `❌ Error ${response.status}: No se pudo cambiar el estado.`
        );
    }
  } catch (error) {
    console.error(`❌ Error en setAntennaPowerStatusService para antena ${antennaId}:`, error);

    if (axios.isAxiosError(error)) {
      if (error.code === "ECONNABORTED") {
        throw new Error("⏱️ Tiempo de espera agotado.");
      }
      if (error.code === "ERR_NETWORK") {
        throw new Error("🌐 Error de conexión. Verifique su conexión a internet.");
      }
      if (error.response) {
        const { status, data } = error.response;
        const errorMsg = data?.msm || data?.message || `Error ${status}: ${error.message}`;
        throw new Error(errorMsg);
      }
      if (!error.response) {
        throw new Error("🔌 No se pudo conectar con el servidor.");
      }
    }

    throw new Error(error.message || "❌ Error desconocido al cambiar el estado de la antena.");
  }
};
